
import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { ProjectDocument } from '../types.ts';
import { scanIntelligence } from '../services/geminiService.ts';
import { Loader2, CheckCircle2, AlertTriangle, Upload, File, FileText, FileImage, FileSpreadsheet, Download, Trash2, ScanLine } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export const DocumentsPanel: React.FC<{ projectId: number }> = ({ projectId }) => {
  const documents = useStore(() => api.documents.list(d => d.projectId === projectId), [projectId], 'documents');
  const fileInput = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error' | 'loading'; message: string } | null>(null);
  const [scanningId, setScanningId] = useState<number | null>(null);

  useEffect(() => {
    if (uploadStatus?.type === 'success' || uploadStatus?.type === 'error') {
      const timer = setTimeout(() => setUploadStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [uploadStatus]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const extractTextFromPdf = async (base64Data: string): Promise<string> => {
    try {
      // Convert base64 to typed array
      const binaryString = window.atob(base64Data.split(',')[1]);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        fullText += `--- Page ${i} ---\n${pageText}\n\n`;
      }

      return fullText;
    } catch (error) {
      console.error('PDF extraction failed:', error);
      return '';
    }
  };

  const processFiles = async (files: FileList) => {
    setUploadStatus({ type: 'loading', message: 'Processing assets & extracting intelligence...' });
    let successCount = 0;
    let errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} exceeds 10MB limit.`);
        continue;
      }

      try {
        const base64Data = await fileToBase64(file);
        let extractedText = '';

        // Extract text logic
        if (file.type === 'application/pdf') {
          extractedText = await extractTextFromPdf(base64Data);
        } else if (file.type === 'text/plain' || file.name.endsWith('.md') || file.type === 'application/json') {
          // For text files, decode from base64
          try {
            extractedText = window.atob(base64Data.split(',')[1]);
          } catch (e) {
            console.warn('Text decoding failed', e);
          }
        }

        await api.documents.add({
          projectId: projectId,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedAt: new Date().toISOString(),
          data: base64Data,
          textContent: extractedText // Save extracted text
        });
        successCount++;
      } catch (e) {
        console.error(e);
        errors.push(`Failed to ingest ${file.name}.`);
      }
    }

    if (errors.length > 0) {
      setUploadStatus({ type: 'error', message: `Ingested ${successCount}. Note: ${errors.join(' ')}` });
    } else if (successCount > 0) {
      setUploadStatus({ type: 'success', message: `Successfully registered ${successCount} build assets.` });
    } else {
      setUploadStatus(null);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await processFiles(e.target.files);
    }
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleScan = async (doc: ProjectDocument) => {
    if (!doc.id) return;
    setScanningId(doc.id);
    try {
      const { risks, deadlines, wbs } = await scanIntelligence(doc.data as string, doc.fileType);

      for (const r of risks) {
        await api.risks.add({
          projectId: projectId,
          summary: r.summary,
          level: r.level as any || 'Medium',
          mitigationPlan: r.mitigationPlan
        });
      }

      for (const d of deadlines) {
        await api.complianceRequirements.add({
          projectId: projectId,
          title: d.title,
          dueDate: d.dueDate,
          requirementType: d.requirementType,
          status: 'Pending',
          description: d.description
        });
      }

      for (const task of wbs) {
        await api.wbsTasks.add({
          projectId: projectId,
          wbsCode: task.wbsCode || 'N/A',
          title: task.title || 'Extracted Task',
          status: 'Not Started',
          description: task.description
        });
      }

      setUploadStatus({
        type: 'success',
        message: `Extraction complete: Found ${risks.length} risks, ${deadlines.length} compliance items, and ${wbs.length} tasks.`
      });
    } catch (err: any) {
      setUploadStatus({ type: 'error', message: 'Intelligence extraction node error.' });
    } finally {
      setScanningId(null);
    }
  };

  const getIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText size={20} className="text-rose-500" />;
    if (type.includes('image')) return <FileImage size={20} className="text-indigo-500" />;
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return <FileSpreadsheet size={20} className="text-emerald-500" />;
    return <File size={20} className="text-zinc-400" />;
  }

  const isScannable = (type: string) => ['application/pdf', 'text/plain'].includes(type);

  return (
    <div className="space-y-6">
      {uploadStatus && (
        <div className={`p-6 rounded-[2rem] border flex items-center gap-4 animate-in fade-in slide-in-from-top-4 shadow-xl ${uploadStatus.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-900' :
          uploadStatus.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-900' :
            'bg-zinc-900 border-zinc-800 text-white'
          }`}>
          {uploadStatus.type === 'loading' ? <Loader2 className="animate-spin" size={24} /> :
            uploadStatus.type === 'success' ? <CheckCircle2 size={24} className="text-emerald-500" /> : <AlertTriangle size={24} className="text-rose-500" />}
          <span className="font-black text-sm tracking-tight uppercase tracking-widest">{uploadStatus.message}</span>
        </div>
      )}

      <div
        className={`bg-white p-12 rounded-[2.5rem] border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center text-center cursor-pointer ${isDragOver ? 'border-black bg-zinc-50 scale-[1.01]' : 'border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50'
          }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setIsDragOver(false); processFiles(e.dataTransfer.files); }}
        onClick={() => fileInput.current?.click()}
      >
        <input type="file" ref={fileInput} className="hidden" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" onChange={handleUpload} />
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all shadow-sm ${isDragOver ? 'bg-black text-white' : 'bg-white text-zinc-300 border border-zinc-100'}`}>
          <Upload size={24} />
        </div>
        <h3 className={`text-xl font-black mb-1 uppercase tracking-tight ${isDragOver ? 'text-black' : 'text-zinc-900'}`}>
          {isDragOver ? 'Deposit to Vault' : 'Job Asset Deposit'}
        </h3>
        <p className="text-[10px] text-zinc-400 font-black uppercase tracking-widest max-w-sm">
          Contracts, scopes, and site photos generate automated hierarchies.
        </p>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-zinc-300 overflow-hidden shadow-xl shadow-zinc-200/50">

        <div className="px-10 py-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/20">
          <h3 className="font-black text-lg text-black flex items-center gap-3 uppercase tracking-tighter">
            <FileText size={20} className="text-zinc-400" /> Operational Intelligence Vault
          </h3>
          <span className="text-[10px] font-black uppercase text-zinc-400 tracking-widest">{documents?.length || 0} Assets Indexed</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-zinc-50/50 text-[10px] text-zinc-400 font-black uppercase tracking-widest border-b border-zinc-100">
              <tr>
                <th className="px-10 py-4">Asset Descriptor</th>
                <th className="px-10 py-4">Status / Date</th>
                <th className="px-10 py-4 text-right">Operational Scan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {documents?.map(doc => (
                <tr key={doc.id} className="hover:bg-zinc-50/50 transition-colors group">
                  <td className="px-10 py-5 font-black text-black flex items-center gap-3">
                    {getIcon(doc.fileType)}
                    <span className="truncate max-w-[300px]" title={doc.fileName}>{doc.fileName}</span>
                    {doc.textContent && (
                      <div className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md flex items-center gap-1.5 border border-blue-100" title="Content Indexed for AI RAG">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-[8px] font-black uppercase tracking-widest">Indexed</span>
                      </div>
                    )}
                  </td>
                  <td className="px-10 py-5 text-zinc-400 font-black uppercase tracking-widest">{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                  <td className="px-10 py-5 text-right flex justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleScan(doc); }}
                      disabled={scanningId === doc.id || !isScannable(doc.fileType)}
                      className={`p-2 rounded-xl transition-all border ${!isScannable(doc.fileType) ? 'text-zinc-100 border-zinc-50 opacity-10 cursor-not-allowed' :
                        scanningId === doc.id ? 'bg-black text-white border-black' : 'text-zinc-300 border-zinc-100 hover:text-black hover:border-black'
                        }`}
                    >
                      {scanningId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
                    </button>
                    <button onClick={() => api.documents.delete(doc.id!)} className="p-2 text-zinc-100 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
